import { trpc, trpcRetry } from "@carrot/bff-client"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useLocation } from "react-router-dom"
import { useTelemedicineAccess } from "../booking/hooks"
import { isAppointmentScenario } from "./mock/scenarios"
import {
  APPOINTMENT_SCENARIO_QUERY_PARAM,
  useCancelAppointment as useFixtureCancelAppointment,
  useMockAppointments,
  type MockCancelAppointmentResult
} from "./mock/useMockAppointments"
import type { AppointmentsPageData, PastAppointment, Provider, UpcomingAppointment } from "./types"

export type AppointmentsResult = {
  data: AppointmentsPageData | undefined
  isLoading: boolean
  isError: boolean
}

const APPOINTMENTS_QUERY_KEY = "telemedicine.getAppointments"
const JOIN_LINK_QUERY_KEY = "telemedicine.getAppointmentJoinLink"

type GetAppointmentsResult = Awaited<ReturnType<typeof trpc["telemedicine"]["getAppointments"]["query"]>>
type ApiAppointment = NonNullable<GetAppointmentsResult["upcoming"]>
type ApiProvider = GetAppointmentsResult["specialists"][number]

// `?apptState=<scenario>` forces a fixture (QA); real data otherwise. The live queries are disabled
// under the override, so the fixture path makes no network call.
function useIsMockOverride(): boolean {
  const { search } = useLocation()
  return isAppointmentScenario(new URLSearchParams(search).get(APPOINTMENT_SCENARIO_QUERY_PARAM))
}

const toProvider = (provider: ApiProvider): Provider => ({
  name: provider.name,
  title: provider.credential ?? undefined,
  photoUrl: provider.photoUrl ?? null
})

// formsComplete isn't on scheduling-api's list (Healthie forms), and the join link is fetched
// just-in-time by UpNextCard — both are placeholders here.
const toUpcoming = (appointment: ApiAppointment): UpcomingAppointment => ({
  id: appointment.id,
  provider: toProvider(appointment.provider),
  appointmentType: appointment.appointmentType,
  date: new Date(appointment.startTime),
  formsComplete: false,
  joinUrl: "",
  status: "scheduled"
})

const toPast = (appointment: ApiAppointment): PastAppointment => ({
  id: appointment.id,
  provider: toProvider(appointment.provider),
  appointmentType: appointment.appointmentType,
  date: appointment.startTime.slice(0, 10),
  status: "completed"
})

export function useAppointments(): AppointmentsResult {
  const isMockOverride = useIsMockOverride()
  const mock = useMockAppointments()
  const access = useTelemedicineAccess({ enabled: !isMockOverride })
  const appointments = useQuery({
    queryKey: [APPOINTMENTS_QUERY_KEY],
    queryFn: () => trpc.telemedicine.getAppointments.query(),
    enabled: !isMockOverride,
    refetchOnWindowFocus: false,
    retry: trpcRetry(1)
  })

  if (isMockOverride) {
    return { data: mock.data, isLoading: false, isError: false }
  }

  const isError = access.isError || appointments.isError
  if (isError || !access.data || !appointments.data) {
    return { data: undefined, isLoading: !isError, isError }
  }

  return {
    data: {
      eligible: access.data.programs.some((program) => program.qualified),
      upcoming: appointments.data.upcoming ? toUpcoming(appointments.data.upcoming) : null,
      past: appointments.data.past.map(toPast),
      specialists: appointments.data.specialists.map(toProvider)
    },
    isLoading: false,
    isError: false
  }
}

/**
 * The just-in-time visit join link, enabled only when the caller says the visit is imminent:
 * `connection.joinUrl` is transient PHI absent from the appointment list. Returns null when Healthie
 * has not provisioned a link. Disabled under the fixture override.
 */
export function useJoinLink(appointmentId: string, options: { enabled: boolean }): { joinUrl: string | null } {
  const isMockOverride = useIsMockOverride()
  const query = useQuery({
    queryKey: [JOIN_LINK_QUERY_KEY, appointmentId],
    queryFn: () => trpc.telemedicine.getAppointmentJoinLink.query({ appointmentId }),
    enabled: options.enabled && !isMockOverride && appointmentId.length > 0,
    refetchOnWindowFocus: false,
    retry: trpcRetry(1),
    staleTime: 0,
    cacheTime: 0
  })
  return { joinUrl: query.data?.joinUrl ?? null }
}

/**
 * Cancel an appointment: real writeback by default, the in-memory fixture cancel under a `?apptState`
 * override. On success the appointments query is invalidated so the cancelled visit drops out.
 */
export function useCancelAppointment(): MockCancelAppointmentResult {
  const isMockOverride = useIsMockOverride()
  const fixtureCancel = useFixtureCancelAppointment()
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (appointmentId: string) => trpc.telemedicine.cancelAppointment.mutate({ appointmentId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [APPOINTMENTS_QUERY_KEY] }),
    // Errors surface via `isError`; onError keeps the rejected mutation observed (no unhandled reject).
    onError: () => undefined
  })

  if (isMockOverride) {
    return fixtureCancel
  }
  // Fire-and-forget with a success callback (not mutateAsync): a failed cancel never calls onSuccess,
  // so the dialog can't report a false "canceled", and `isError` drives the error message.
  return {
    mutate: (appointmentId, options) => mutation.mutate(appointmentId, { onSuccess: options?.onSuccess }),
    isLoading: mutation.isPending,
    isError: mutation.isError
  }
}
